import React, { useEffect, useState, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import { useParams } from "react-router-dom";
import numWords from "num-words";
import { toast } from "react-toastify";

import { DownloadAndPrint } from "../../components/DownloadAndPrint";
import { Box, Heading, Image, Text } from "../../components/elements";
import { get_date } from "../../components/generalComponets";
import { FinanceService } from "../../services";
import { CardLayout } from "../v3";
import { Table, Tbody, Td, Th, Thead, Tr } from "../elements/Table";
import { CompanyLogo } from "../../components/shared";

interface ClientReceiptData {
  id?: string;
  date?: string;
  amount?: number;
  description?: string;
  company?: {
    name: string;
    primaryAddress?: { locality?: string };
    primaryPhone?: string;
    primaryEmail?: string;
  };
  source?: {
    name: string;
    primaryPhone?: string;
  };
  project?: { name: string };
}

export const ClientReceiptInfo = () => {
  const [income, setIncome] = useState<Partial<ClientReceiptData> | null>(null);

  const componentRef = useRef<HTMLDivElement>(null);
  const { id } = useParams();

  useEffect(() => {
    fetchData(id);
  }, [id]);

  const fetchData = async (incomeId) => {
    try {
      const { data } = await FinanceService.getIncomeById(incomeId);
      setIncome(data);
    } catch (error) {
      console.error("🚀 ~ fetchData ~ error:", error);
      toast.error("Failed to fetch income details.");
    }
  };

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    pageStyle: `@page {
            size: A4;
             margin: 8mm 8mm 0mm 0mm;
          }
          @media print {
            body {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;

            }
          }`,
  });

  return (
    <Box sx={{ mt: -9, zIndex: 2, position: "relative" }}>
      <CardLayout sx={{ padding: 0 }}>
        <div style={styles.container} ref={componentRef}>
          <Box style={styles.leftColumn}>
            <Box style={styles.companyBox}>
              <CompanyLogo type="full" alt="Company Logo" style={styles.companyLogo} plain={true} />
              {/* Company Details */}
              <Heading as="h5" style={styles.companyName}>
                {income?.company?.name}
              </Heading>
              <Text style={styles.companyDetails}>{income?.company?.primaryAddress?.locality}</Text>
              <Text style={styles.companyDetails}>Phone: {income?.company?.primaryPhone}</Text>
              <Text style={styles.companyDetails}>Email: {income?.company?.primaryEmail}</Text>
            </Box>
          </Box>

          <Box style={styles.rightColumn}>
            <Box style={styles.invoiceBox}>
              <Box style={styles.invoiceHeader}>
                <Box style={styles.invoiceDetails}>
                  <Heading as="h5" style={styles.fontStyle}>
                    To:
                  </Heading>
                  <Text style={styles.fontStyle}>{income?.source?.name}</Text>
                  <Text style={styles.fontStyle}>Phone: {income?.source?.primaryPhone}</Text>
                  <Text
                    style={{
                      ...styles.topMargin,
                      ...styles.fontStyle,
                    }}
                  >
                    Project: {income?.project?.name}
                  </Text>
                </Box>
                <Box style={styles.leftAlign}>
                  <Text style={styles.fontStyle}>Date: {get_date(income?.date)}</Text>
                  <Text style={styles.fontStyle}>Receipt ID: #{income?.id}</Text>
                </Box>
              </Box>

              {/* Center Part - Table */}
              <Table style={styles.table}>
                <Thead style={styles.thead}>
                  <Tr style={styles.trHead}>
                    <Th style={styles.thDescription}>Description</Th>
                    <Th
                      style={{
                        ...styles.thAmount,
                        ...styles.amountText,
                      }}
                    >
                      Amount
                    </Th>
                  </Tr>
                </Thead>
                <Tbody style={styles.fontStyle}>
                  <Tr style={styles.trBody}>
                    <Td style={styles.tdDescription}>{income?.description}</Td>
                    <Td style={styles.amountText}>{Number(income?.amount)}</Td>
                  </Tr>
                </Tbody>
              </Table>

              {/* Bottom Part */}

              <Box
                style={{
                  ...styles.footer,
                  ...styles.fontStyle,
                  ...styles.topMargin,
                }}
              >
                <Box style={styles.forRupeesContainer}>
                  <Text as="span">{`Rupees ${numWords(income?.amount)} only`}</Text>
                </Box>
                <Box style={styles.forCompanyContainer}>
                  <Text as="span" style={styles.forText}>
                    For
                  </Text>
                  <br />
                  <Text as="span" style={styles.forCompanyName}>
                    {income?.company?.name}
                  </Text>
                </Box>
              </Box>
            </Box>
          </Box>
        </div>
      </CardLayout>

      {/* Print Button */}
      <DownloadAndPrint handlePrint={handlePrint} printRef={componentRef} fileName={"ClientReceiptInfo.pdf"} />
    </Box>
  );
};

// Inline styles
const styles = {
  container: {
    display: "flex",
    flexDirection: "row",
    padding: "25px",
  },
  leftColumn: {
    flex: "1 0 33.33%",
    borderRight: "1px solid #B2BEB5",
  },
  companyBox: {
    padding: "20px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
  },
  companyLogo: {
    // TODO: adjust the logo size as needed
    maxWidth: "70%",
    maxHeight: "150px",
    objectFit: "contain",
    marginBottom: "10px",
  },
  companyName: {
    fontSize: "14px",
    margin: "5px 0",
    fontWeight: "bold",
  },
  companyDetails: {
    fontSize: "12px",
    margin: "2px 0",
    fontWeight: "bold",
  },
  rightColumn: {
    flex: "3 0 66.67%",
  },
  invoiceBox: {
    paddingLeft: "20px",
  },
  invoiceHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  invoiceDetails: {
    flex: 1,
    marginTop: "50px",
  },
  fontStyle: {
    fontWeight: "bold",
    fontSize: "0.72rem",
  },
  colPadding: {
    paddingTop: "15px",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  thead: {
    fontSize: "0.8rem",
    textTransform: "uppercase",
    letterSpacing: "1px",
    fontWeight: "bold",
  },
  trHead: {
    borderTop: "0.5px solid #B2BEB5",
    borderBottom: "0.5px solid #B2BEB5",
  },
  thDescription: {
    textAlign: "left",
    width: "80%",
    padding: "6px",
    borderRight: "0.5px solid #B2BEB5",
  },
  amountText: {
    textAlign: "left",
    padding: "6px",
  },
  thAmount: {
    width: "20%",
  },
  trBody: {
    borderBottom: "0.5px solid #B2BEB5",
  },
  tdDescription: {
    padding: "6px",
    borderRight: "0.5px solid #B2BEB5",
  },
  topMargin: {
    marginTop: "15px",
  },
  leftAlign: {
    textAlign: "left",
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
  },
  forRupeesContainer: {
    textAlign: "left",
  },
  forCompanyContainer: {
    textAlign: "left",
    paddingTop: "40px",
    paddingRight: "10px",
  },
  forText: {
    fontWeight: "bold",
  },
  forCompanyName: {
    fontWeight: "bold",
    whiteSpace: "nowrap",
  },
};
